/**
 * UpcomingMilestonesPanel — milestones on the projects I'm a member of, due in
 * the next 60 days (soonest first). Self-scoped by intersecting all milestones
 * with my project IDs from `getMyListView()`. Overdue milestones surface at the
 * top with a red flag.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  Badge,
  Card,
  Group,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@/components/ui';
import { IconFlag } from '@tabler/icons-react';
import {
  useProjectService,
  useProjectMilestoneService,
  type ProjectModelView,
  type ProjectMilestone,
} from '@actschurch/ccms-sdk';
import { PROJECTS_ROUTES } from '@/config/routes';
import { parseBackendDate } from '../../utils/projectHelpers';
import { MEMBER_DASHBOARD_KEY } from './dashboardConstants';

interface MilestoneRow {
  milestone: ProjectMilestone;
  projectTitle: string;
  due: Date;
  overdue: boolean;
  daysAway: number;
}

export function UpcomingMilestonesPanel() {
  const navigate = useNavigate();
  const projectService = useProjectService();
  const milestoneService = useProjectMilestoneService();

  const { data: myProjects = [], isLoading: projectsLoading, error: projectsError } = useQuery({
    queryKey: [...MEMBER_DASHBOARD_KEY, 'my-projects'],
    queryFn: () => projectService.getMyListView(),
  });
  const { data: milestones = [], isLoading: milestonesLoading, error: milestonesError } = useQuery({
    queryKey: [...MEMBER_DASHBOARD_KEY, 'milestones'],
    queryFn: () => milestoneService.getAll(),
  });

  const isLoading = projectsLoading || milestonesLoading;

  const rows = useMemo<MilestoneRow[]>(() => {
    const today = dayjs().startOf('day');
    const horizon = today.add(60, 'day');
    const titleById = new Map<number, string>();
    (myProjects as ProjectModelView[]).forEach((p) => {
      if (!p.isArchived) titleById.set(p.projectID, p.title || `Project ${p.projectID}`);
    });

    return (milestones as ProjectMilestone[])
      .reduce<MilestoneRow[]>((acc, m) => {
        const projectTitle = titleById.get(m.projectID);
        if (!projectTitle) return acc;
        const dueDate = parseBackendDate(m.dueDate);
        if (!dueDate) return acc;
        const d = dayjs(dueDate);
        if (d.isAfter(horizon)) return acc;
        acc.push({
          milestone: m,
          projectTitle,
          due: dueDate,
          overdue: d.isBefore(today),
          daysAway: d.diff(today, 'day'),
        });
        return acc;
      }, [])
      .sort((a, b) => a.due.getTime() - b.due.getTime())
      .slice(0, 6);
  }, [myProjects, milestones]);

  return (
    <Card withBorder padding="lg" radius="md">
      <Group gap="xs" mb="sm">
        <ThemeIcon size="md" variant="light" color="teal" radius="xl">
          <IconFlag size={16} />
        </ThemeIcon>
        <Title order={5}>Upcoming Milestones</Title>
      </Group>

      {isLoading ? (
        <Stack gap="xs">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} height={40} radius="sm" />
          ))}
        </Stack>
      ) : projectsError || milestonesError ? (
        <Text size="sm" c="red" ta="center" py="md">
          Could not load milestones:{' '}
          {((projectsError || milestonesError) as Error)?.message ?? 'service unavailable'}
        </Text>
      ) : rows.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="md">
          No milestones due in the next 60 days.
        </Text>
      ) : (
        <Stack gap="xs">
          {rows.map(({ milestone, projectTitle, due, overdue, daysAway }) => (
            <Group
              key={milestone.milestoneID}
              justify="space-between"
              wrap="nowrap"
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(PROJECTS_ROUTES.VIEW(milestone.projectID))}
            >
              <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                <Text size="sm" fw={500} truncate>
                  {milestone.title}
                </Text>
                <Text size="xs" c="dimmed" truncate>
                  {projectTitle}
                </Text>
              </Stack>
              <Stack gap={2} align="flex-end">
                <Text size="xs" c={overdue ? 'red' : 'dimmed'} fw={overdue ? 600 : undefined}>
                  {dayjs(due).format('DD MMM')}
                </Text>
                <Badge size="sm" variant="light" color={overdue ? 'red' : 'teal'}>
                  {overdue
                    ? `${Math.abs(daysAway)}d overdue`
                    : daysAway === 0
                      ? 'Today'
                      : `${daysAway}d`}
                </Badge>
              </Stack>
            </Group>
          ))}
        </Stack>
      )}
    </Card>
  );
}
